import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, EyeSlash, ArrowRight, ArrowLeft, Spinner } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const MARKETING_URL = 'https://herchainsolutions.com';

const ROLE_ROUTES = {
  vendor: '/vendor-portal',
  freight_forwarder: '/freight-portal',
  buyer: '/buyer-portal',
  department_head: '/dh-portal',
  receiver: '/receiver-portal',
  accounts_payable: '/receiver-portal',
  storekeeper: '/storekeeper-portal',
  financial_controller: '/fc-approval',
  internal_requester: '/internal-requester-portal',
  purchasing_manager: '/dashboard',
  system_admin: '/dashboard',
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const SignInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, logout } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [view, setView] = useState('signin'); // 'signin' | 'forgot'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Logout any existing session when visiting /login (parity with /demo behavior)
  useEffect(() => {
    if (user && location.pathname === '/login') {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      const userData = await login(email.trim().toLowerCase(), password);
      if (userData && userData.role) {
        toast.success(`Welcome, ${userData.full_name}`);
        const route = ROLE_ROUTES[userData.role] || '/dashboard';
        navigate(route, { replace: true });
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err) {
      // Custom message per spec — always show the same friendly text on 401
      const status = err?.response?.status;
      if (status === 401) {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(formatApiErrorDetail(err?.response?.data?.detail) || 'Sign in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail.trim().toLowerCase() });
      setForgotSent(true);
    } catch (err) {
      // We intentionally never reveal whether the email exists — show neutral message regardless
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const PurpleHeader = () => (
    <header className="text-center" data-testid="signin-header">
      <h1 className="font-bold tracking-tight" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
        <span className="text-[#3D1D72] text-4xl sm:text-5xl">HERChain</span>{' '}
        <span className="text-[#D4C5F0] text-4xl sm:text-5xl">Solutions</span>
      </h1>
      <p className="mt-3 text-sm sm:text-base text-slate-600" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
        Procurement Management Built for Caribbean Hospitality
      </p>
    </header>
  );

  const Footer = () => (
    <footer className="mt-10 text-center text-xs text-slate-500" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }} data-testid="signin-footer">
      <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-3">
        <Link to="/privacy-policy" className="hover:text-[#3D1D72]" data-testid="footer-privacy-link">Privacy Policy</Link>
        <span className="text-slate-300">·</span>
        <Link to="/terms-of-service" className="hover:text-[#3D1D72]" data-testid="footer-terms-link">Terms of Service</Link>
        <span className="text-slate-300">·</span>
        <a href={MARKETING_URL} className="hover:text-[#3D1D72]" data-testid="footer-marketing-link">
          herchainsolutions.com
        </a>
        <span className="text-slate-300">·</span>
        <Link to="/demo" className="hover:text-[#3D1D72]" data-testid="footer-view-demo-link">
          View Demo
        </Link>
      </nav>
      <p>© {new Date().getFullYear()} HERChain Solutions. All rights reserved.</p>
    </footer>
  );

  return (
    <div
      className="min-h-screen bg-white flex items-center justify-center px-4 py-10"
      style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
      data-testid="signin-page"
    >
      <div className="w-full max-w-md">
        <PurpleHeader />

        <div
          className="mt-8 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8"
          style={{ boxShadow: '0 1px 3px rgba(61, 29, 114, 0.05), 0 8px 24px rgba(61, 29, 114, 0.06)' }}
        >
          {view === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-5" data-testid="signin-form" noValidate>
              <h2 className="text-xl font-bold text-[#3D1D72]" data-testid="signin-title">Sign in</h2>

              <div className="space-y-1.5">
                <label htmlFor="signin-email" className="block text-sm font-medium text-slate-700">Email address</label>
                <input
                  id="signin-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  className="w-full h-11 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#3D1D72]/30 focus:border-[#3D1D72] text-sm"
                  data-testid="signin-email-input"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="signin-password" className="block text-sm font-medium text-slate-700">Password</label>
                <div className="relative">
                  <input
                    id="signin-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full h-11 px-3 pr-11 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#3D1D72]/30 focus:border-[#3D1D72] text-sm"
                    data-testid="signin-password-input"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-[#3D1D72]"
                    data-testid="signin-toggle-password"
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" role="alert" data-testid="signin-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-lg bg-[#3D1D72] hover:bg-[#2D1652] text-white font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="signin-submit-btn"
              >
                {loading ? <Spinner size={18} className="animate-spin" /> : null}
                {loading ? 'Signing in…' : 'Login'}
              </button>

              <div className="flex flex-col items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setView('forgot'); setForgotEmail(email); setForgotSent(false); }}
                  className="text-sm text-[#3D1D72] hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
                <a
                  href={MARKETING_URL}
                  className="text-xs text-slate-500 hover:text-[#3D1D72] inline-flex items-center gap-1"
                  data-testid="learn-more-link"
                >
                  Learn more at herchainsolutions.com
                  <ArrowRight size={12} />
                </a>
              </div>
            </form>
          )}

          {view === 'forgot' && !forgotSent && (
            <form onSubmit={handleForgot} className="space-y-5" data-testid="forgot-form" noValidate>
              <button
                type="button"
                onClick={() => setView('signin')}
                className="text-sm text-[#3D1D72] hover:underline inline-flex items-center gap-1"
                data-testid="forgot-back-link"
              >
                <ArrowLeft size={14} /> Back to sign in
              </button>
              <h2 className="text-xl font-bold text-[#3D1D72]">Forgot your password?</h2>
              <p className="text-sm text-slate-600">
                Enter the email associated with your account. We&apos;ll send you a link to reset your password.
                The link expires in 24&nbsp;hours.
              </p>
              <div className="space-y-1.5">
                <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-700">Email address</label>
                <input
                  id="forgot-email"
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  className="w-full h-11 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#3D1D72]/30 focus:border-[#3D1D72] text-sm"
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={forgotLoading || !forgotEmail}
                className="w-full h-11 rounded-lg bg-[#3D1D72] hover:bg-[#2D1652] text-white font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="forgot-submit-btn"
              >
                {forgotLoading ? <Spinner size={18} className="animate-spin" /> : null}
                {forgotLoading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )}

          {view === 'forgot' && forgotSent && (
            <div className="space-y-4" data-testid="forgot-sent-confirmation">
              <h2 className="text-xl font-bold text-[#3D1D72]">Check your email</h2>
              <p className="text-sm text-slate-700">
                If an account with <span className="font-medium">{forgotEmail}</span> exists, we&apos;ve sent a password reset link.
                Please check your inbox (and spam folder). The link expires in 24&nbsp;hours.
              </p>
              <button
                type="button"
                onClick={() => { setView('signin'); setForgotSent(false); }}
                className="w-full h-11 rounded-lg bg-[#3D1D72] hover:bg-[#2D1652] text-white font-semibold transition-colors"
                data-testid="forgot-back-to-signin-btn"
              >
                Back to sign in
              </button>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default SignInPage;
